import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../../components/elements/Loader";
import SnackbarAlert from "../../components/elements/SnackbarAlert";
import useLandingAuthApi from "../../services/api/useLandingAuthApi";
import PaginationComponent from "../ListOfTripRequest/views/PaginationComponent";
import HistoricTripsHeader from "./HistoricTripsHeader";
import HistoricTripsFilter from "./views/HistoricTripsFilter";
import ListOfHistoricTrip from "./views/ListOfHistoricTrip";
import ErrorPopup from "../../components/elements/ErrorPopup";
import SearchBar from "../ListOfTripRequest/views/SearchBar";
import ROUTE_PATHS from "../../utils/routePaths";
import { HISTORIC_TRIPS_SEARCH_TERM_KEY } from "../../utils/searchStorage";
import "./css/HistoricTripsFilter.css";

const downloadVarianceReportFile = (data, filename = "historic-trips.xlsx") => {
  const url = window.URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const getExportFilename = (filters) => {
  const datePart = filters?.selectedDateFrom
    ? (filters.selectedDateFrom.format
      ? filters.selectedDateFrom.format("YYYY-MM-DD")
      : filters.selectedDateFrom)
    : dayjs().format("YYYY-MM-DD");
  const timePart = dayjs().format("HHmmss");
  return `historic-trips-${datePart}-${timePart}.xlsx`;
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const buildExportData = (activeFilters, searchTerm) => {
  const data = {};
  if (activeFilters?.selectedArrivalAirport) {
    data.arrivalAirportId = activeFilters.selectedArrivalAirport;
  }
  if (activeFilters?.selectedDepartureAirport) {
    data.departureAirportId = activeFilters.selectedDepartureAirport;
  }
  if (activeFilters?.selectedDateFrom) {
    data.departureDate = activeFilters.selectedDateFrom.format
      ? activeFilters.selectedDateFrom.format("YYYY-MM-DD")
      : activeFilters.selectedDateFrom;
  }
  if (activeFilters?.selectedStatus === "EXEMPTED") {
    data.isExempted = "true";
  } else if (activeFilters?.selectedStatus) {
    data.tripStatus = activeFilters.selectedStatus;
  }
  if (activeFilters?.registrationNumber?.trim()) {
    data.registrationNumber =
      activeFilters.registrationNumber.trim();
  }
  const trimmedSearchTerm = (searchTerm || "").trim();
  if (isValidEmail(trimmedSearchTerm)) {
    data.email = trimmedSearchTerm;
  }
  return data;
};

const HistoricTripsPage = () => {
  const {
    fetchHistoricTrips,
    fetchHistoricTripByFilters,
    searchHistoricTrip,
    exportHistoricTrips,
  } = useLandingAuthApi();

  const location = useLocation();
  const initialPage = parseInt(location.state?.currentPage) || 1;
  const [currentPage, setCurrentPage] = useState(initialPage);
  const itemsPerPage = 10;
  const [activeFilters, setActiveFilters] = useState(null);
  const [searchTerm, setSearchTerm] = useState(
    localStorage.getItem(HISTORIC_TRIPS_SEARCH_TERM_KEY) || ""
  );
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const navigate = useNavigate();

  const downloadVarianceReportMutation = useMutation({
    mutationFn: (filters) =>
      exportHistoricTrips(buildExportData(filters, searchTerm)),
    onSuccess: (response, filters) => {
      if (response) {
        downloadVarianceReportFile(response, getExportFilename(filters));
      }
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 4000);
    },
  });

  const {
    data: tripData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "historic-trips",
      currentPage,
      itemsPerPage,
      searchTerm.length > 3 ? searchTerm : "list",
      activeFilters,
    ],
    queryFn: async () => {
      if (searchTerm && searchTerm.length > 3) {
        try {
          const result = await searchHistoricTrip(searchTerm);
          return result;
        } catch (error) {
          if (error.status === 404) {
            return null;
          }

          throw error;
        }
      }
      if (activeFilters) {
        return fetchHistoricTripByFilters(
          currentPage - 1,
          itemsPerPage,
          activeFilters
        );
      }
      return fetchHistoricTrips(currentPage - 1, itemsPerPage);
    },
    keepPreviousData: true,
    retry: false,
  });

  let trips = [];
  if (tripData) {
    if (searchTerm && searchTerm.length > 3 && tripData?.data) {
      trips = [tripData.data];
    }
    if (tripData?.data?.content) {
      trips = tripData.data?.content || [];
    }
  }
  const totalPages = tripData?.data?.totalPages || 1;
  const totalElements = tripData?.data?.totalElements || 0;
  const cumulativeCount = Math.min(currentPage * itemsPerPage, totalElements);

  const persistSearchTerm = (term) => {
    if (term) {
      localStorage.setItem(HISTORIC_TRIPS_SEARCH_TERM_KEY, term);
    } else {
      localStorage.removeItem(HISTORIC_TRIPS_SEARCH_TERM_KEY);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFilterApplied = (filters) => {
    setActiveFilters(filters);
    setCurrentPage(1);
    setSearchTerm("");
    persistSearchTerm("");
  };

  const handleFilterCleared = () => {
    setActiveFilters(null);
    setCurrentPage(1);
  };

  const handleSearch = (searchText) => {
    if (searchText) {
      setActiveFilters(null);
      setCurrentPage(1);
    }
    setSearchTerm(searchText);
    persistSearchTerm(searchText);
  };

  const handleQrScanSuccess = async (searchText) => {
    try {
      const result = await searchHistoricTrip(searchText);
      if (result?.data) {
        navigate(ROUTE_PATHS.HISTORIC_TRIP_DETAILS(result?.data?.id), {
          state: { currentPage: currentPage },
        });
      }
    } catch (error) {
      console.error("Error searching trip:", error);

      setSearchTerm("");
      persistSearchTerm("");
    }
  };
  const isSearchEmailValid = isValidEmail((searchTerm || "").trim());
  const handleExportFromHeader = () => {
    if (!isSearchEmailValid || downloadVarianceReportMutation.isPending) {
      return;
    }
    downloadVarianceReportMutation.mutate(activeFilters || {});
  };
  return (
    <div>
      <HistoricTripsHeader
        currentPage={currentPage}
        onExportRequest={handleExportFromHeader}
        isExportPending={downloadVarianceReportMutation.isPending}
        isExportDisabled={!isSearchEmailValid}
      />
      <SearchBar
        onSearch={handleSearch}
        onQrScanSuccess={handleQrScanSuccess}
        value={searchTerm}
        placeholder="Search by Name, Email ID, or Trip ID"
      />
      <HistoricTripsFilter
        onFilterApplied={handleFilterApplied}
        onFilterCleared={handleFilterCleared}
        resetFilters={!!searchTerm}
      />
      {error && error.status !== 404 && (
        <div className="trip-request-error-message">
          <ErrorPopup children={error.message}></ErrorPopup>
        </div>
      )}
      {error && error.status === 404 && (
        <div className="no-trips-message-container">No trips available</div>
      )}
      {!error && (
        <>
          <div className="trip-request-row">
            <div className="trip-request-label">
              <label>All Trips</label>
            </div>

            {trips.length > 0 && !searchTerm && !isLoading && (
              <div className="trip-result">
                <p>
                  {isLoading
                    ? "Loading..."
                    : `Showing ${cumulativeCount} of ${totalElements}`}
                </p>
              </div>
            )}
          </div>
          {isLoading && trips.length === 0 && <Loader />}
          {trips.length === 0 && !isLoading && (
            <div className="no-trips-message-container">No trips available</div>
          )}
          {trips.length > 0 && (
            <ListOfHistoricTrip trips={trips} currentPage={currentPage} />
          )}
        </>
      )}
      {trips.length > 0 && totalPages > 1 && (
        <PaginationComponent
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
      {showSuccessMessage && (
        <SnackbarAlert type="success">
          Historic trips have been exported successfully.
        </SnackbarAlert>
      )}
      {downloadVarianceReportMutation.isError && (
        <SnackbarAlert type="error">
          {downloadVarianceReportMutation.error?.message ||
            "Failed to download historic trips"}
        </SnackbarAlert>
      )}
    </div>
  );
};
export default HistoricTripsPage;
